import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import { SegmentHeaderProps } from './interfaces';
import SegmentSpeakerDropdown from './SegmentSpeakerDropdown';
import { defaultTheme } from './theme';

/**
 * Formats time as MM:SS
 * @param seconds Time in seconds
 * @returns Formatted time string
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Header component for transcript segments
 * Shows speaker identification and segment start time
 */
const SegmentHeader: React.FC<SegmentHeaderProps> = memo(({
  segment,
  editorStore
}) => {
  // Get speaker color from the speaker service with safe fallback
  const speakerColor = useMemo(() => {
    // Safely access the speaker service to avoid TypeScript errors
    const speakerService = editorStore.editor?.speakerService;
    if (!speakerService) return defaultTheme.colors.text;
    
    // Get the color with fallback to a default
    return speakerService.getSpeakerColor?.(segment.speakerLabel) || defaultTheme.colors.text;
  }, [editorStore.editor?.speakerService, segment.speakerLabel]);
  
  return (
    <SegmentHeaderWrapper 
    contentEditable="false" 
    suppressContentEditableWarning={true}
    data-start-time={segment.startTime}
    data-end-time={segment.endTime}
    data-segment-id={segment.id}
    data-speaker-label={segment.speakerLabel}
    >
      <SpeakerInfo>
        <SpeakerLabel 
          className="non-editable"
          contentEditable="false" 
          suppressContentEditableWarning={true} 
          color={speakerColor}
        >
          <SegmentSpeakerDropdown
            segment={segment}
            editorStore={editorStore}
            color={speakerColor}
          />
        </SpeakerLabel>
        <SegmentTime 
          className="non-editable"
          contentEditable="false" 
          suppressContentEditableWarning={true}
        >
          {formatTime(segment.startTime)}
        </SegmentTime>
      </SpeakerInfo>
    </SegmentHeaderWrapper>
  );
});

SegmentHeader.displayName = 'SegmentHeader';

export default SegmentHeader;


/**
 * Header for a transcript segment containing speaker info
 */
export const SegmentHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  cursor: pointer;
  border-bottom: 1px solid transparent;
  margin-bottom: ${defaultTheme.spacing.xs};
`;

/**
 * Container for speaker information
 */
export const SpeakerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${defaultTheme.spacing.md};
`;

/**
 * Speaker label with customizable color
 */
export const SpeakerLabel = styled.div<{ color?: string }>`
  color: ${props => props.color || defaultTheme.colors.text};
  font-weight: 500;
`;

/**
 * Display for segment timestamp
 */
export const SegmentTime = styled.div`
  color: ${defaultTheme.colors.textSecondary};
  font-size: ${defaultTheme.fontSizes.small};
`;
